import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Brain, Server, Monitor, Cloud, Database, Shield, Wrench } from 'lucide-react';
import ExpertiseFan from './ui/expertise-fan';
import { skillCategories } from '../data/siteData';

const categoryIcons = {
  'ai-automation': Brain,
  backend: Server,
  frontend: Monitor,
  'cloud-devops': Cloud,
  'data-engineering': Database,
  security: Shield,
  'business-delivery': Wrench,
};

const FAN_LAYOUT_QUERY = '(max-width: 1199px), (max-width: 1366px) and (any-pointer: coarse)';

const Expertise = () => {
  const [isFanLayout, setIsFanLayout] = useState(() => window.matchMedia?.(FAN_LAYOUT_QUERY).matches ?? false);

  useEffect(() => {
    const query = window.matchMedia?.(FAN_LAYOUT_QUERY);
    if (!query) return;
    const update = () => setIsFanLayout(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  const trackRef = useRef(null);
  const groupRef = useRef(null);
  const pauseRef = useRef({ hover: false, focus: false, dragging: false });
  const offsetRef = useRef(0);
  const widthRef = useRef(0);
  const pointerRef = useRef(null);
  const interactionUntilRef = useRef(0);

  const moveBy = useCallback(delta => {
    const width = widthRef.current;
    if (!width || !trackRef.current) return;
    offsetRef.current = ((offsetRef.current + delta) % width + width) % width;
    trackRef.current.style.transform = `translate3d(${-offsetRef.current}px, 0, 0)`;
  }, []);

  const finishDrag = event => {
    if (pointerRef.current?.id !== event.pointerId) return;
    pointerRef.current = null;
    pauseRef.current.dragging = false;
    event.currentTarget.removeAttribute('data-dragging');
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    interactionUntilRef.current = performance.now() + 1200;
  };

  useEffect(() => {
    if (isFanLayout) return;
    offsetRef.current = 0;
    pointerRef.current = null;
    pauseRef.current = { hover: false, focus: false, dragging: false };
    trackRef.current.style.transform = 'none';
    trackRef.current.parentElement.removeAttribute('data-dragging');
    let frame;
    let previous;
    const measure = () => { widthRef.current = groupRef.current?.getBoundingClientRect().width || 0; };
    measure();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (groupRef.current) observer?.observe(groupRef.current);
    window.addEventListener('resize', measure);
    const tick = time => {
      const elapsed = previous === undefined ? 0 : Math.min(time - previous, 64);
      previous = time;
      const stopped = Object.values(pauseRef.current).some(Boolean) || time < interactionUntilRef.current;
      if (!stopped) moveBy(elapsed * .022);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    const viewport = trackRef.current.parentElement;
    const wheel = event => {
      if (!event.shiftKey && Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
      event.preventDefault();
      const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? viewport.clientWidth : 1;
      moveBy((event.deltaX || event.deltaY) * unit);
      interactionUntilRef.current = performance.now() + 1200;
    };
    viewport.addEventListener('wheel', wheel, { passive: false });
    return () => {
      viewport.removeEventListener('wheel', wheel);
      cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [moveBy, isFanLayout]);

  const renderCards = () => skillCategories.map(category => {
    const Icon = categoryIcons[category.id] || Wrench;
    return (
      <article className="expertise-card" key={category.id}>
        <div className="expertise-card-header">
          <div className="expertise-icon"><Icon size={24} aria-hidden="true" /></div>
          <h3>{category.title}</h3>
        </div>
        <div className="expertise-tags">
          {category.skills.map(skill => <span key={skill} className="expertise-tag">{skill}</span>)}
        </div>
      </article>
    );
  });

  return (
    <section id="expertise" className="expertise-section">
      <div className="expertise-content">
        <div className="container">
        <motion.div className="section-header" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: .6 }}>
          <h2>Technical Expertise</h2>
          <p>Core competencies across engineering, AI, and business systems</p>
        </motion.div>
        </div>
        {isFanLayout ? <div className="container"><ExpertiseFan categories={skillCategories} icons={categoryIcons} /></div> : <>
        <div className="expertise-carousel" role={isFanLayout ? undefined : 'region'} tabIndex={isFanLayout ? undefined : 0} aria-label={isFanLayout ? undefined : 'Technical expertise carousel'} aria-describedby={isFanLayout ? undefined : 'expertise-carousel-help'}
          onPointerEnter={event => { if (event.pointerType === 'mouse') pauseRef.current.hover = true; }}
          onPointerLeave={event => { if (event.pointerType === 'mouse') pauseRef.current.hover = false; }}
          onPointerDown={event => {
            if (isFanLayout || !event.isPrimary || event.button !== 0) return;
            pointerRef.current = { id: event.pointerId, x: event.clientX, startX: event.clientX, startY: event.clientY, horizontal: false };
            pauseRef.current.dragging = true;
            event.currentTarget.setPointerCapture(event.pointerId);
          }}
          onPointerMove={event => {
            const pointer = pointerRef.current;
            if (!pointer || pointer.id !== event.pointerId) return;
            const dx = event.clientX - pointer.startX;
            const dy = event.clientY - pointer.startY;
            if (!pointer.horizontal) {
              if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 6) { finishDrag(event); return; }
              if (Math.abs(dx) < 6) return;
              pointer.horizontal = true;
              event.currentTarget.setAttribute('data-dragging', 'true');
            }
            moveBy(pointer.x - event.clientX);
            pointer.x = event.clientX;
          }}
          onPointerUp={finishDrag}
          onPointerCancel={finishDrag}
          onLostPointerCapture={finishDrag}
          onKeyDown={event => {
            if (isFanLayout || !['ArrowLeft', 'ArrowRight', 'Home'].includes(event.key)) return;
            event.preventDefault();
            const card = groupRef.current.querySelector('.expertise-card');
            const step = card.getBoundingClientRect().width + parseFloat(getComputedStyle(groupRef.current).gap);
            moveBy(event.key === 'Home' ? -offsetRef.current : event.key === 'ArrowRight' ? step : -step);
          }}
          onFocusCapture={event => { pauseRef.current.focus = event.currentTarget.matches(':focus-visible'); }}
          onBlurCapture={event => {
            if (!event.currentTarget.contains(event.relatedTarget)) {
              pauseRef.current.focus = false;
            }
          }}>
          <div className="expertise-carousel-track" ref={trackRef}>
            <div className="expertise-grid" ref={groupRef}>{renderCards()}</div>
            {!isFanLayout && <div className="expertise-grid" aria-hidden="true">{renderCards()}</div>}
          </div>
        </div>
        <p id="expertise-carousel-help" className="expertise-carousel-help">Drag to explore · Use ← → keys</p>
        </>}
      </div>
    </section>
  );
};

export default Expertise;
